/**
 * DailyDouble — the wager moment.
 *
 * When the player picks a Daily Double tile, the board fades to a deep blue
 * field with the "Daily Double" wordmark in warm gold, a brief particle
 * bloom (CSS-driven motes), and a wager UI below: three quick-pick presets
 * + a manual stepper. The player can wager anywhere from the tile's dollar
 * floor (so the tile is always "worth its rung") up to max(current score,
 * the tile's value).
 */
import { motion } from 'motion/react';
import { useCallback } from 'react';

import { formatDollars, type BoardTileState } from '@/games/categories/engine';
import { CATEGORY_LABELS } from '@/games/categories/boards';

import { bloomSpring } from './motion';

import './DailyDouble.css';

export interface DailyDoubleProps {
  tile: BoardTileState;
  score: number;
  currentWager: number;
  onSetWager: (amount: number) => void;
  onConfirm: () => void;
}

export function DailyDouble({
  tile,
  score,
  currentWager,
  onSetWager,
  onConfirm,
}: DailyDoubleProps): React.JSX.Element {
  const minWager = tile.value;
  const maxWager = Math.max(minWager, score);

  const setQuick = useCallback(
    (mode: 'min' | 'half' | 'max') => {
      let amount: number = minWager;
      if (mode === 'half') amount = Math.max(minWager, Math.floor(maxWager / 2));
      if (mode === 'max') amount = maxWager;
      onSetWager(amount);
    },
    [minWager, maxWager, onSetWager],
  );

  return (
    <motion.section
      className="tb-dd"
      initial={{ scale: 0.84, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={bloomSpring}
      aria-label="Daily Double"
    >
      <div className="tb-dd__bloom" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="tb-dd__mote"
            style={
              {
                '--mote-angle': `${(i / 14) * 360}deg`,
                '--mote-delay': `${i * 24}ms`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>

      <motion.h2
        className="tb-dd__title"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1, ...bloomSpring }}
      >
        DAILY DOUBLE
      </motion.h2>

      <motion.p
        className="tb-dd__sub"
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
      >
        Category: <strong>{CATEGORY_LABELS[tile.category]}</strong>
      </motion.p>

      <motion.div
        className="tb-dd__wager"
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.42, duration: 0.4 }}
      >
        <p className="tb-dd__wager-prompt">How much do you wager?</p>

        <div className="tb-dd__amount" aria-live="polite">
          {formatDollars(currentWager)}
        </div>

        <div className="tb-dd__quick" role="group" aria-label="Wager presets">
          <button
            type="button"
            className="tb-dd__quick-btn"
            onClick={() => setQuick('min')}
            data-active={currentWager === minWager ? 'true' : 'false'}
          >
            Minimum
            <span className="tb-dd__quick-amt">{formatDollars(minWager)}</span>
          </button>
          <button
            type="button"
            className="tb-dd__quick-btn"
            onClick={() => setQuick('half')}
            data-active={
              currentWager === Math.max(minWager, Math.floor(maxWager / 2)) ? 'true' : 'false'
            }
          >
            Half
            <span className="tb-dd__quick-amt">
              {formatDollars(Math.max(minWager, Math.floor(maxWager / 2)))}
            </span>
          </button>
          <button
            type="button"
            className="tb-dd__quick-btn"
            onClick={() => setQuick('max')}
            data-active={currentWager === maxWager ? 'true' : 'false'}
          >
            Max
            <span className="tb-dd__quick-amt">{formatDollars(maxWager)}</span>
          </button>
        </div>

        <p className="tb-dd__note">
          A wrong answer won&apos;t lose your score — but a right one wins you the wager.
        </p>

        <button
          type="button"
          className="tb-dd__confirm"
          onClick={onConfirm}
          data-testid="dd-confirm"
        >
          Show me the clue
        </button>
      </motion.div>
    </motion.section>
  );
}
